import { html, LitElement, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import type { TemplateResult } from 'lit';
// Styles live in a real .css file - see data-table.ts for the rationale.
import styles from './data-table-paginator.styles.css?inline';

/**
 * @event {{ index : Number }} page-change - Emitted when the tree item expands.
 *
 * @tag data-table-paginator
 */
export class DataTablePaginator extends LitElement {
  static styles = unsafeCSS(styles);

  @property({ type: Number })
  index = 1;
  @property({ type: Number })
  count = 1;

  attributeTrue(name: string) {
    if (!this.hasAttribute(name)) return false;
    else {
      const attr = this.getAttribute(name);
      return attr === undefined || attr !== 'false';
    }
  }

  render() {
    let currentPage = this.index;
    if (typeof currentPage === 'string') currentPage = parseInt(currentPage);
    const pageCount = this.count;

    const visiblePages = 11;
    const pageDeductor = 5;
    let firstDisplayPage = currentPage - pageDeductor;
    if (firstDisplayPage < 0) firstDisplayPage = 0;
    let lastDisplayPage = firstDisplayPage + visiblePages;
    if (lastDisplayPage > pageCount) lastDisplayPage = pageCount;

    if (
      lastDisplayPage === pageCount &&
      lastDisplayPage - firstDisplayPage + 1 < visiblePages
    )
      firstDisplayPage = Math.max(0, lastDisplayPage - visiblePages + 1);
    if (
      firstDisplayPage === 0 &&
      lastDisplayPage - firstDisplayPage + 1 < visiblePages
    )
      lastDisplayPage = Math.min(
        pageCount,
        firstDisplayPage + visiblePages - 1
      );

    const includeSkipToFirst = firstDisplayPage > 0;
    const includeSkipToLast = lastDisplayPage < pageCount;

    const clickEvent = (e: Event) => {
      e.preventDefault();
      const strPage = (e.target as HTMLUListElement).textContent?.toLowerCase();
      if (strPage === 'prev') currentPage--;
      else if (strPage === 'next') currentPage++;
      else if (strPage === 'last') currentPage = pageCount - 1;
      else if (strPage === 'first') currentPage = 0;
      else currentPage = parseInt(strPage ?? '1') - 1;

      //handle accidental overflows
      currentPage = Math.min(pageCount, Math.max(0, currentPage));
      const event = new CustomEvent('page-change', {
        detail: {
          index: currentPage,
        },
      });
      this.dispatchEvent(event);
    };

    const pages = (): TemplateResult[] => {
      const pageResult: TemplateResult[] = [];
      for (let i = firstDisplayPage; i < lastDisplayPage; i++) {
        if (i === currentPage) {
          pageResult.push(
            html` <li class="page-item active">
              <span class="page-link">${i + 1}</span>
            </li>`
          );
        } else {
          pageResult.push(
            html` <li class="page-item">
              <a class="page-link" href="#" @click=${clickEvent}>${i + 1}</a>
            </li>`
          );
        }
      }
      return pageResult;
    };

    const skipFirstClass = `page-item ${!includeSkipToFirst ? 'disabled' : ''}`;
    const currentPageClass = `page-item ${currentPage === 0 ? 'disabled' : ''}`;
    const lastPageClass = `page-item ${currentPage === pageCount - 1 ? 'disabled' : ''}`;
    const skipToLastClass = `page-item ${!includeSkipToLast ? 'disabled' : ''}`;
    return html` <nav aria-label="Page navigation">
      <ul class="pagination justify-content-center">
        <li class=${skipFirstClass}>
          <a class="page-link" href="#" @click=${clickEvent}>First</a>
        </li>
        <li class=${currentPageClass}>
          <a class="page-link" href="#" rel="prev" @click=${clickEvent}>Prev</a>
        </li>
        ${pages()}
        <li class=${lastPageClass}>
          <a class="page-link" href="#" rel="prev" @click=${clickEvent}>Next</a>
        </li>
        <li class=${skipToLastClass}>
          <a class="page-link" href="#" @click=${clickEvent}>Last</a>
        </li>
      </ul>
    </nav>`;
  }
}

export const PAGINATOR_TAG = 'data-table-paginator';

if (!customElements.get(PAGINATOR_TAG)) {
  customElements.define(PAGINATOR_TAG, DataTablePaginator);
}
